import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/apiConfig';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    FormControl,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    CircularProgress,
    Alert,
    Grid,
    Divider,
    Select,
    MenuItem,
    InputLabel,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    InputAdornment
} from '@mui/material';
import { getClientDataReport, getNewUsersReport, getAvailableFields, getVisitCountReport, getChildrenVisitCountReport, getDemographicsReport, getPlanPurchaseReport, getClassAttendanceReport, getContractExpiryReport, getMemberLTVReport, getPeakHoursAnalysis, getCreditInfoReport, getClientLifecycleReport, getClassTypeAttendanceReport, getRevenueAnalysisReport, getContractPriceReport, getPaymentHolidayReport } from '../api/reportApi';
import axios from 'axios';
import SendMessageModal from './SendMessageModal';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import EmailIcon from '@mui/icons-material/Email';
import { getAffiliateById } from '../api/affiliateApi';
import { formatCurrency } from '../utils/currency';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);


const REPORT_TYPES = {
    CLIENT_DATA: 'client-data',
    NEW_USERS: 'new-users',
    VISIT_COUNT: 'visit-count',
    CHILDREN_VISIT_COUNT: 'children-visit-count',
    DEMOGRAPHICS: 'demographics',
    PLAN_PURCHASE: 'plan-purchase',
    CLASS_ATTENDANCE: 'class-attendance',
    CLASS_TYPE_ATTENDANCE: 'class-type-attendance',
    CONTRACT_EXPIRY: 'contract-expiry',
    MEMBER_LTV: 'member-ltv',
    REVENUE_ANALYSIS: 'revenue-analysis',
    PEAK_HOURS: 'peak-hours',
    CREDIT_INFO: 'credit-info',
    CLIENT_LIFECYCLE: 'client-lifecycle',
    CONTRACT_PRICE: 'contract-price',
    PAYMENT_HOLIDAY: 'payment-holiday'
};

const REPORT_TYPE_LABELS = {
    [REPORT_TYPES.CLIENT_DATA]: 'Client Data Report',
    [REPORT_TYPES.NEW_USERS]: 'New Users Report',
    [REPORT_TYPES.VISIT_COUNT]: 'Visit Count Report',
    [REPORT_TYPES.CHILDREN_VISIT_COUNT]: 'Children Visit Count Report',
    [REPORT_TYPES.DEMOGRAPHICS]: 'Demographics Report',
    [REPORT_TYPES.PLAN_PURCHASE]: 'Plan Purchase Report',
    [REPORT_TYPES.CLASS_ATTENDANCE]: 'Class Attendance Report',
    [REPORT_TYPES.CLASS_TYPE_ATTENDANCE]: 'Class Type Attendance Report',
    [REPORT_TYPES.CONTRACT_EXPIRY]: 'Contract Expiry Report',
    [REPORT_TYPES.MEMBER_LTV]: 'Member Lifetime Value',
    [REPORT_TYPES.REVENUE_ANALYSIS]: 'Revenue Analysis Report',
    [REPORT_TYPES.PEAK_HOURS]: 'Peak Hours Analysis',
    [REPORT_TYPES.CREDIT_INFO]: 'Credit Info Report',
    [REPORT_TYPES.CLIENT_LIFECYCLE]: 'Client Lifecycle Report',
    [REPORT_TYPES.CONTRACT_PRICE]: 'Contracts by Price',
    [REPORT_TYPES.PAYMENT_HOLIDAY]: 'Payment Holidays'
};

const REPORT_CATEGORIES = [
    {
        id: 'members',
        label: 'Members',
        types: [
            REPORT_TYPES.CLIENT_DATA,
            REPORT_TYPES.NEW_USERS,
            REPORT_TYPES.VISIT_COUNT,
            REPORT_TYPES.CHILDREN_VISIT_COUNT,
            REPORT_TYPES.DEMOGRAPHICS,
            REPORT_TYPES.CLASS_TYPE_ATTENDANCE
        ]
    },
    {
        id: 'finance',
        label: 'Finance',
        types: [
            REPORT_TYPES.PLAN_PURCHASE,
            REPORT_TYPES.CONTRACT_EXPIRY,
            REPORT_TYPES.MEMBER_LTV,
            REPORT_TYPES.REVENUE_ANALYSIS,
            REPORT_TYPES.CONTRACT_PRICE,
            REPORT_TYPES.PAYMENT_HOLIDAY,
            REPORT_TYPES.CREDIT_INFO
        ]
    },
    {
        id: 'general',
        label: 'General',
        types: [
            REPORT_TYPES.CLASS_ATTENDANCE,
            REPORT_TYPES.PEAK_HOURS,
            REPORT_TYPES.CLIENT_LIFECYCLE
        ]
    }
];

const CLASS_TYPES = ["WOD", "Weightlifting", "Cardio", "Rowing", "Gymnastics", "Open Gym", "Kids", "Basic", "Hyrox", "Circuit", "Strength", "Hybrid", "Mobility", "Other"];

const PRICE_FILTER_MODES = [
    { value: 'all', label: 'All prices', description: 'Every active contract' },
    { value: 'exact', label: 'Exact price', description: 'One specific amount' },
    { value: 'range', label: 'Price range', description: 'Minimum to maximum' }
];

const parseReportDate = (value) => {
    if (value == null || value === '') {
        return null;
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatReportDate = (value, emptyValue = '-') => {
    const date = parseReportDate(value);
    if (!date) {
        return emptyValue;
    }

    return new Intl.DateTimeFormat('et-EE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(date);
};

const formatReportDateTime = (value, emptyValue = '-') => {
    const date = parseReportDate(value);
    if (!date) {
        return emptyValue;
    }

    return new Intl.DateTimeFormat('et-EE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

export default function AffiliateReports({ affiliateId, affiliateEmail }) {
    const [reportType, setReportType] = useState(REPORT_TYPES.CLIENT_DATA);
    const [reportCategory, setReportCategory] = useState('members');
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 1);
        return date.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [availableFields, setAvailableFields] = useState([]);
    const [selectedFields, setSelectedFields] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [orderBy, setOrderBy] = useState('');
    const [order, setOrder] = useState('asc');
    const [minVisits, setMinVisits] = useState(3);
    const [demographicFilters, setDemographicFilters] = useState([
        { sex: 'all', minAge: null, maxAge: null }
    ]);
    const [filterType, setFilterType] = useState('all');
    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [planPurchaseTotal, setPlanPurchaseTotal] = useState(0);
    const [planDetails, setPlanDetails] = useState(null);
    const [lifecycleData, setLifecycleData] = useState(null);
    const [abcPlanId, setAbcPlanId] = useState('');
    const [classType, setClassType] = useState('Hyrox');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [guestReportData, setGuestReportData] = useState([]);
    const [sendMessageModalOpen, setSendMessageModalOpen] = useState(false);
    const [messageOptionsOpen, setMessageOptionsOpen] = useState(false);
    const [messageTarget, setMessageTarget] = useState('selectedUsers');
    const [revenueData, setRevenueData] = useState(null);
    const [includeVAT, setIncludeVAT] = useState(false);
    const [paymentCurrency, setPaymentCurrency] = useState('EUR');
    const [priceFilterMode, setPriceFilterMode] = useState('all');
    const [exactPrice, setExactPrice] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [showContractDetails, setShowContractDetails] = useState(false);
    const [onlyActiveContracts, setOnlyActiveContracts] = useState(false);
    const [contractPriceSummary, setContractPriceSummary] = useState([]);
    const [contractPriceTotals, setContractPriceTotals] = useState(null);
    const [paymentHolidayTotals, setPaymentHolidayTotals] = useState(null);
    const newUsersForMessaging = reportType === REPORT_TYPES.NEW_USERS
        ? reportData
            .filter((row) => row.userId && row.email)
            .map((row) => ({
                id: row.userId,
                fullName: row.fullName || row.email,
                email: row.email,
                hasActivePlan: typeof row.hasActivePlan === 'boolean'
                    ? row.hasActivePlan
                    : (typeof row.isActive === 'boolean' ? row.isActive : false)
            }))
        : [];
    const inactiveNewUsersForMessaging = newUsersForMessaging.filter((user) => !user.hasActivePlan);
    const guestUsersForMessaging = guestReportData.map((guest) => ({
        id: guest.id,
        firstName: guest.firstName,
        lastName: guest.lastName,
        email: guest.email
    }));
    const contractPriceRecipients = reportType === REPORT_TYPES.CONTRACT_PRICE
        ? Array.from(new Map(
            reportData
                .filter((row) => row.userId && row.email)
                .map((row) => [row.userId, {
                    id: row.userId,
                    fullName: row.fullName || row.email,
                    email: row.email
                }])
        ).values())
        : [];

    useEffect(() => {
        if (reportType !== REPORT_TYPES.DEMOGRAPHICS && reportType !== REPORT_TYPES.PLAN_PURCHASE && reportType !== REPORT_TYPES.CLIENT_LIFECYCLE && reportType !== REPORT_TYPES.CLASS_TYPE_ATTENDANCE && reportType !== REPORT_TYPES.REVENUE_ANALYSIS && reportType !== REPORT_TYPES.CONTRACT_PRICE && reportType !== REPORT_TYPES.PAYMENT_HOLIDAY) {
            loadAvailableFields();
        }
        if (reportType === REPORT_TYPES.PLAN_PURCHASE || reportType === REPORT_TYPES.CLIENT_LIFECYCLE) {
            loadPlans();
        }
        // Reset selected users when changing report type
        setSelectedUsers([]);
    }, [reportType]);

    const loadPlans = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/plans`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            if (response.data) {
                setPlans(response.data);
            }
        } catch (err) {
            console.error('Error loading plans:', err);
            setError('Failed to load plans');
        }
    };

    const loadAvailableFields = async () => {
        try {
            setIsLoading(true);
            const result = await getAvailableFields(reportType);
            if (result && result.success && result.fields) {
                setAvailableFields(result.fields);
                const defaultFields = reportType === REPORT_TYPES.CLIENT_DATA
                    ? ['fullName', 'email', 'phone', 'address', 'isActive', 'visitCount']
                    : reportType === REPORT_TYPES.VISIT_COUNT
                        ? ['fullName', 'email', 'phone', 'visitCount', 'isActive']
                        : reportType === REPORT_TYPES.CHILDREN_VISIT_COUNT
                            ? ['fullName', 'parentName', 'parentEmail', 'parentPhone', 'visitCount', 'isActive']
                        : ['fullName', 'email', 'phone', 'createdAt', 'isActive'];

                setSelectedFields(defaultFields.filter(field =>
                    result.fields.some(f => f.key === field)
                ));
            } else {
                setError(result?.error || 'Failed to load available fields');
            }
        } catch (err) {
            setError('Error loading available fields');
            console.error('Error loading fields:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReportTypeChange = (newReportType) => {
        setReportType(newReportType);
        const matchedCategory = REPORT_CATEGORIES.find((category) => category.types.includes(newReportType));
        if (matchedCategory) {
            setReportCategory(matchedCategory.id);
        }
        setReportData([]);
        setGuestReportData([]);
        setError('');
        setPlanPurchaseTotal(0);
        setPlanDetails(null);
        setSelectedPlanId('');
        setRevenueData(null);
        setContractPriceSummary([]);
        setContractPriceTotals(null);
        setPriceFilterMode('all');
        setExactPrice('');
        setMinPrice('');
        setMaxPrice('');
        setShowContractDetails(false);
        setOnlyActiveContracts(false);
        setPaymentHolidayTotals(null);
    };

    const handleFieldChange = (fieldKey) => {
        setSelectedFields(prev => {
            if (prev.includes(fieldKey)) {
                return prev.filter(f => f !== fieldKey);
            } else {
                return [...prev, fieldKey];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedFields.length === availableFields.length) {
            setSelectedFields([]);
        } else {
            setSelectedFields(availableFields.map(f => f.key));
        }
    };

    const generateReport = async (overrides = {}) => {
        const needsFields = ![REPORT_TYPES.DEMOGRAPHICS, REPORT_TYPES.PLAN_PURCHASE, REPORT_TYPES.CLASS_ATTENDANCE,
                            REPORT_TYPES.CONTRACT_EXPIRY, REPORT_TYPES.MEMBER_LTV, REPORT_TYPES.REVENUE_ANALYSIS, REPORT_TYPES.PEAK_HOURS,
                            REPORT_TYPES.CREDIT_INFO, REPORT_TYPES.CLIENT_LIFECYCLE, REPORT_TYPES.CLASS_TYPE_ATTENDANCE,
                            REPORT_TYPES.CONTRACT_PRICE, REPORT_TYPES.PAYMENT_HOLIDAY].includes(reportType);
        
        if (needsFields && selectedFields.length === 0) {
            setError('Please select at least one field');
            return;
        }

        const needsDates = [REPORT_TYPES.NEW_USERS, REPORT_TYPES.VISIT_COUNT, REPORT_TYPES.CHILDREN_VISIT_COUNT, REPORT_TYPES.PLAN_PURCHASE,
                           REPORT_TYPES.CLASS_ATTENDANCE, REPORT_TYPES.CLASS_TYPE_ATTENDANCE, REPORT_TYPES.CONTRACT_EXPIRY,
                           REPORT_TYPES.MEMBER_LTV, REPORT_TYPES.REVENUE_ANALYSIS, REPORT_TYPES.PEAK_HOURS, REPORT_TYPES.CLIENT_LIFECYCLE,
                           REPORT_TYPES.PAYMENT_HOLIDAY].includes(reportType);
        
        if (needsDates) {
            if (!startDate || !endDate) {
                setError('Please select both start and end dates');
                return;
            }

            if (new Date(startDate) > new Date(endDate)) {
                setError('Start date cannot be after end date');
                return;
            }
        }

        if (reportType === REPORT_TYPES.PLAN_PURCHASE && !selectedPlanId) {
            setError('Please select a plan');
            return;
        }

        if (reportType === REPORT_TYPES.CLASS_TYPE_ATTENDANCE && !classType) {
            setError('Please select a class type');
            return;
        }

        if (reportType === REPORT_TYPES.CONTRACT_PRICE) {
            if (priceFilterMode === 'exact' && (exactPrice === '' || Number(exactPrice) < 0 || Number.isNaN(Number(exactPrice)))) {
                setError('Please enter a valid exact price');
                return;
            }
            if (priceFilterMode === 'range') {
                if (minPrice === '' && maxPrice === '') {
                    setError('Please enter a minimum price, a maximum price, or both');
                    return;
                }
                if (minPrice !== '' && (Number(minPrice) < 0 || Number.isNaN(Number(minPrice)))) {
                    setError('Please enter a valid minimum price');
                    return;
                }
                if (maxPrice !== '' && (Number(maxPrice) < 0 || Number.isNaN(Number(maxPrice)))) {
                    setError('Please enter a valid maximum price');
                    return;
                }
                if (minPrice !== '' && maxPrice !== '' && Number(minPrice) > Number(maxPrice)) {
                    setError('Minimum price cannot be greater than maximum price');
                    return;
                }
            }
        }

        setIsLoading(true);
        setError('');
        setSelectedUsers([]);

        try {
            let result;

            if (reportType === REPORT_TYPES.CLIENT_DATA) {
                result = await getClientDataReport(selectedFields);
            } else if (reportType === REPORT_TYPES.NEW_USERS) {
                result = await getNewUsersReport(startDate, endDate, selectedFields);
            } else if (reportType === REPORT_TYPES.VISIT_COUNT) {
                result = await getVisitCountReport(startDate, endDate, minVisits, selectedFields);
            } else if (reportType === REPORT_TYPES.CHILDREN_VISIT_COUNT) {
                result = await getChildrenVisitCountReport(startDate, endDate, minVisits, selectedFields);
            } else if (reportType === REPORT_TYPES.DEMOGRAPHICS) {
                result = await getDemographicsReport(filterType, demographicFilters);
            } else if (reportType === REPORT_TYPES.PLAN_PURCHASE) {
                result = await getPlanPurchaseReport(startDate, endDate, selectedPlanId);
            } else if (reportType === REPORT_TYPES.CLASS_ATTENDANCE) {
                result = await getClassAttendanceReport(startDate, endDate);
            } else if (reportType === REPORT_TYPES.CLASS_TYPE_ATTENDANCE) {
                result = await getClassTypeAttendanceReport(startDate, endDate, classType);
            } else if (reportType === REPORT_TYPES.CONTRACT_EXPIRY) {
                result = await getContractExpiryReport(startDate, endDate);
            } else if (reportType === REPORT_TYPES.MEMBER_LTV) {
                result = await getMemberLTVReport(startDate, endDate);
            } else if (reportType === REPORT_TYPES.REVENUE_ANALYSIS) {
                result = await getRevenueAnalysisReport(startDate, endDate);
            } else if (reportType === REPORT_TYPES.PEAK_HOURS) {
                result = await getPeakHoursAnalysis(startDate, endDate);
            } else if (reportType === REPORT_TYPES.CREDIT_INFO) {
                result = await getCreditInfoReport();
            } else if (reportType === REPORT_TYPES.CLIENT_LIFECYCLE) {
                result = await getClientLifecycleReport(startDate, endDate, abcPlanId || null);
            } else if (reportType === REPORT_TYPES.CONTRACT_PRICE) {
                result = await getContractPriceReport({
                    exactPrice: priceFilterMode === 'exact' ? exactPrice : undefined,
                    minPrice: priceFilterMode === 'range' ? minPrice : undefined,
                    maxPrice: priceFilterMode === 'range' ? maxPrice : undefined,
                    onlyActive: overrides.onlyActive !== undefined ? overrides.onlyActive : onlyActiveContracts
                });
            } else if (reportType === REPORT_TYPES.PAYMENT_HOLIDAY) {
                result = await getPaymentHolidayReport(startDate, endDate);
            }

            if (result && result.success) {
                if (reportType === REPORT_TYPES.CLIENT_LIFECYCLE) {
                    setLifecycleData(result.data);
                    setPlans(result.availablePlans || []);
                } else if (reportType === REPORT_TYPES.REVENUE_ANALYSIS) {
                    setRevenueData(result);
                    setReportData(result.monthlyBreakdown || []);
                    setGuestReportData([]);
                    setLifecycleData(null);
                    setContractPriceSummary([]);
                    setContractPriceTotals(null);
                    setPaymentHolidayTotals(null);
                } else if (reportType === REPORT_TYPES.CONTRACT_PRICE) {
                    setReportData(result.data || []);
                    setContractPriceSummary(result.summary || []);
                    setContractPriceTotals(result.totals || null);
                    setGuestReportData([]);
                    setLifecycleData(null);
                    setRevenueData(null);
                    setPaymentHolidayTotals(null);
                } else if (reportType === REPORT_TYPES.PAYMENT_HOLIDAY) {
                    setReportData(result.data || []);
                    setPaymentHolidayTotals(result.totals || { totalHolidays: 0, totalPeople: 0 });
                    setGuestReportData([]);
                    setLifecycleData(null);
                    setRevenueData(null);
                    setContractPriceSummary([]);
                    setContractPriceTotals(null);
                } else {
                    setReportData(result.data || result.heatmapData || []);
                    if (reportType === REPORT_TYPES.NEW_USERS) {
                        setGuestReportData(result.guestRegistrations || []);
                    } else {
                        setGuestReportData([]);
                    }
                    setRevenueData(null);
                    setContractPriceSummary([]);
                    setContractPriceTotals(null);
                    setPaymentHolidayTotals(null);
                }
                if (reportType === REPORT_TYPES.PLAN_PURCHASE) {
                    setPlanPurchaseTotal(result.totalAmount || 0);
                    setPlanDetails(result.planDetails || null);
                }
            } else {
                setError(result?.error || 'Failed to generate report');
                setReportData([]);
                setGuestReportData([]);
                setLifecycleData(null);
                setRevenueData(null);
                setContractPriceSummary([]);
                setContractPriceTotals(null);
                setPaymentHolidayTotals(null);
            }
        } catch (err) {
            setError('Error generating report');
            console.error('Report generation error:', err);
            setReportData([]);
            setGuestReportData([]);
            setRevenueData(null);
            setContractPriceSummary([]);
            setContractPriceTotals(null);
            setPaymentHolidayTotals(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSort = (field) => {
        const isAsc = orderBy === field && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(field);

        const sortedData = [...reportData].sort((a, b) => {
            let aVal = a[field];
            let bVal = b[field];

            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return newOrder === 'asc' ? aVal - bVal : bVal - aVal;
            }

            if (field.includes('Date') || field.includes('At')) {
                const dateA = new Date(aVal);
                const dateB = new Date(bVal);
                if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    return newOrder === 'asc' ? dateA - dateB : dateB - dateA;
                }
            }

            if (aVal < bVal) {
                return newOrder === 'asc' ? -1 : 1;
            }
            if (aVal > bVal) {
                return newOrder === 'asc' ? 1 : -1;
            }
            return 0;
        });

        setReportData(sortedData);
    };

    const exportToCSV = () => {
        if (!reportData || reportData.length === 0) {
            setError('No data to export');
            return;
        }

        try {
            let headers, csvContent;

            if (reportType === REPORT_TYPES.PLAN_PURCHASE) {
                headers = ['Buyer Name', 'Email', 'Phone', 'Amount', 'Purchase Date', 'Is Family Member'];
                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => {
                        return [
                            row.buyerName || '',
                            row.email || '',
                            row.phone || '',
                            row.amount || '',
                            formatReportDate(row.purchaseDate, ''),
                            row.isFamilyMember ? 'Yes' : 'No'
                        ].map(value => {
                            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                                return `"${value.replace(/"/g, '""')}"`;
                            }
                            return value;
                        }).join(',');
                    })
                ].join('\n');
            } else if (reportType === REPORT_TYPES.CLASS_ATTENDANCE) {
                headers = ['Training Name', 'Type', 'Date & Time', 'Day', 'Trainer', 'Capacity', 'Registered', 'Waitlist', 'Utilization %'];
                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => [
                        row.trainingName, row.trainingType, formatReportDateTime(row.time, ''),
                        row.dayOfWeek, row.trainer, row.capacity, row.registered, row.waitlist, row.utilizationRate
                    ].join(','))
                ].join('\n');
            } else if (reportType === REPORT_TYPES.CONTRACT_EXPIRY) {
                headers = ['Member Name', 'Email', 'Phone', 'Contract Name', 'Payment Amount', 'Next Payment Date', 'Days Until', 'Months Active', 'Status'];
                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => [
                        row.memberName, row.email, row.phone || '', row.contractName, row.paymentAmount.toFixed(2),
                        formatReportDate(row.nextPaymentDate, ''), row.daysUntilExpiry, row.monthsActive, row.status
                    ].join(','))
                ].join('\n');
            } else if (reportType === REPORT_TYPES.MEMBER_LTV) {
                headers = ['Member Name', 'Email', 'Phone', 'Total Spent', 'Months Active', 'Avg Monthly', 'Purchase Count', 'Monthly Recurring', 'Predicted LTV'];
                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => [
                        row.memberName, row.email, row.phone || '', row.totalSpent.toFixed(2), row.monthsActive,
                        row.avgMonthlySpend.toFixed(2), row.purchaseCount, row.monthlyRecurring.toFixed(2), row.predictedLTV.toFixed(2)
                    ].join(','))
                ].join('\n');
            } else if (reportType === REPORT_TYPES.PEAK_HOURS) {
                headers = ['Day', 'Time Slot', 'Class Count', 'Avg Registrations', 'Total Capacity', 'Total Registrations', 'Utilization %'];
                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => [
                        row.day, row.timeSlot, row.classCount, row.avgRegistrations, row.totalCapacity,
                        row.totalRegistrations, row.utilizationRate
                    ].join(','))
                ].join('\n');
            } else if (reportType === REPORT_TYPES.CREDIT_INFO) {
                headers = ['Member Name', 'Email', 'Phone', 'Current Credit', 'Total Added', 'Total Used', 'Transaction Count'];
                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => [
                        row.memberName, row.email, row.phone || '', row.currentCredit.toFixed(2),
                        row.totalAdded.toFixed(2), row.totalUsed.toFixed(2), row.transactionCount
                    ].join(','))
                ].join('\n');
            } else if (reportType === REPORT_TYPES.REVENUE_ANALYSIS) {
                headers = ['Month', 'Contracts Revenue', 'Children Contracts Revenue', 'Plans Revenue', 'Total Revenue'];
                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => [
                        row.label || row.month || '',
                        getDisplayRevenueAmount(row.contracts || 0).toFixed(2),
                        getDisplayRevenueAmount(row.childrenContracts || 0).toFixed(2),
                        getDisplayRevenueAmount(row.plans || 0).toFixed(2),
                        getDisplayRevenueAmount(row.totalRevenue || 0).toFixed(2)
                    ].join(','))
                ].join('\n');
            } else if (reportType === REPORT_TYPES.CLASS_TYPE_ATTENDANCE) {
                headers = ['Name', 'Email', 'Phone', 'Attendance Count'];
                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => {
                        return [
                            row.fullName || '',
                            row.email || '',
                            row.phone || '',
                            row.attendanceCount || 0
                        ].map(value => {
                            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                                return `"${value.replace(/"/g, '""')}"`;
                            }
                            return value;
                        }).join(',');
                    })
                ].join('\n');
            } else if (reportType === REPORT_TYPES.CONTRACT_PRICE) {
                if (showContractDetails) {
                    headers = ['Member Name', 'Email', 'Contract Start', 'Status', 'Price'];
                    csvContent = [
                        headers.join(','),
                        ...reportData.map(row => [
                            row.fullName || '',
                            row.email || '',
                            formatReportDate(row.startDate, ''),
                            row.status || '',
                            Number(row.paymentAmount || 0).toFixed(2)
                        ].map(value => {
                            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                                return `"${value.replace(/"/g, '""')}"`;
                            }
                            return value;
                        }).join(','))
                    ].join('\n');
                } else {
                    headers = ['Price', 'People', 'Contracts', 'Monthly Total'];
                    csvContent = [
                        headers.join(','),
                        ...contractPriceSummary.map(row => [
                            Number(row.price || 0).toFixed(2),
                            row.peopleCount || 0,
                            row.contractCount || 0,
                            Number(row.monthlyTotal || 0).toFixed(2)
                        ].join(','))
                    ].join('\n');
                }
            } else if (reportType === REPORT_TYPES.PAYMENT_HOLIDAY) {
                headers = ['Member Name', 'Start Date', 'End Date', 'Reason'];
                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => [
                        row.fullName || '',
                        formatReportDate(row.startDate, ''),
                        formatReportDate(row.endDate, ''),
                        row.reason || ''
                    ].map(value => {
                        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                            return `"${value.replace(/"/g, '""')}"`;
                        }
                        return value;
                    }).join(','))
                ].join('\n');
            } else if (reportType === REPORT_TYPES.NEW_USERS) {
                const memberHeaders = selectedFields.map(fieldKey => {
                    const field = availableFields.find(f => f.key === fieldKey);
                    return field ? field.label : fieldKey;
                });

                const memberRows = reportData.map(row =>
                    selectedFields.map(field => {
                        let value = row[field];
                        if (value == null || value === undefined) value = '';
                        if (typeof value === 'boolean') value = value ? 'Yes' : 'No';
                        else if (field === 'createdAt' || field.toLowerCase().includes('date') || field.toLowerCase().includes('at')) {
                            value = formatReportDate(value, '');
                        }
                        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                            return `"${value.replace(/"/g, '""')}"`;
                        }
                        return value;
                    }).join(',')
                );

                const guestHeaders = ['Guest First Name', 'Guest Last Name', 'Guest Email', 'Guest Registered At'];
                const guestRows = guestReportData.map(guest => {
                    const values = [
                        guest.firstName || '',
                        guest.lastName || '',
                        guest.email || '',
                        formatReportDate(guest.createdAt, '')
                    ];
                    return values.map(value => {
                        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                            return `"${value.replace(/"/g, '""')}"`;
                        }
                        return value;
                    }).join(',');
                });

                csvContent = [
                    'Members',
                    memberHeaders.join(','),
                    ...memberRows,
                    '',
                    'Guest Registrations',
                    guestHeaders.join(','),
                    ...guestRows
                ].join('\n');
            } else {
                headers = selectedFields.map(fieldKey => {
                    const field = availableFields.find(f => f.key === fieldKey);
                    return field ? field.label : fieldKey;
                });

                csvContent = [
                    headers.join(','),
                    ...reportData.map(row => {
                        return selectedFields.map(field => {
                            let value = row[field];

                            if (value == null || value === undefined) {
                                value = '';
                            } else if (typeof value === 'boolean') {
                                value = value ? 'Yes' : 'No';
                            } else if ((field.includes('Date') || field.includes('At')) && value) {
                                value = formatReportDate(value, value);
                            }

                            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                                value = `"${value.replace(/"/g, '""')}"`;
                            }

                            return value;
                        }).join(',');
                    })
                ].join('\n');
            }

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${reportType}-report-${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError('Failed to export CSV');
            console.error('Export error:', err);
        }
    };

    const formatCellValue = (value, field) => {
        if (value == null || value === undefined) return '-';

        const fieldInfo = availableFields.find(f => f.key === field);

        if (fieldInfo?.type === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        if (fieldInfo?.type === 'date' || field.includes('Date') || field.includes('At')) {
            return formatReportDate(value);
        }

        return String(value);
    };

    const getDisplayRevenueAmount = (amount) => {
        const numericAmount = Number(amount || 0);
        return !includeVAT && revenueData?.vatRate
            ? numericAmount / (1 + Number(revenueData.vatRate) / 100)
            : numericAmount;
    };

    const formatRevenueAmount = (amount) => {
        return formatCurrency(Math.round(getDisplayRevenueAmount(amount)), paymentCurrency);
    };

    const formatMoney = (amount) => formatCurrency(amount, paymentCurrency);

    const addDemographicFilter = () => {
        setDemographicFilters([...demographicFilters, { sex: 'all', minAge: null, maxAge: null }]);
    };

    const removeDemographicFilter = (index) => {
        if (demographicFilters.length > 1) {
            setDemographicFilters(demographicFilters.filter((_, i) => i !== index));
        }
    };

    const updateDemographicFilter = (index, field, value) => {
        const updated = [...demographicFilters];
        updated[index][field] = value;
        setDemographicFilters(updated);
    };

    const handleSelectUser = (user) => {
        const isSelected = selectedUsers.some(u => u.id === user.id);
        if (isSelected) {
            setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
        } else {
            setSelectedUsers([...selectedUsers, user]);
        }
    };

    const handleSelectAllUsers = () => {
        const usersToSelect = reportType === REPORT_TYPES.CONTRACT_PRICE
            ? contractPriceRecipients
            : reportData.map(user => ({
                id: user.userId,
                fullName: user.fullName,
                email: user.email
            }));

        if (selectedUsers.length === usersToSelect.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(usersToSelect);
        }
    };

    const handleSendMessage = (target = 'selectedUsers') => {
        if (target === 'selectedUsers' && selectedUsers.length === 0) {
            setError('Please select at least one user to send a message');
            return;
        }
        if (target === 'contractPriceAll' && contractPriceRecipients.length === 0) {
            setError('No people found to send an email');
            return;
        }
        if (target === 'newMembers' && newUsersForMessaging.length === 0) {
            setError('No users available in New Users report');
            return;
        }
        if (target === 'inactiveNewMembers' && inactiveNewUsersForMessaging.length === 0) {
            setError('No inactive users available in New Users report');
            return;
        }
        if (target === 'newGuests' && guestUsersForMessaging.length === 0) {
            setError('No guest registrations available');
            return;
        }
        if (target === 'allSelectedUsers' && newUsersForMessaging.length === 0 && guestUsersForMessaging.length === 0) {
            setError('No recipients available');
            return;
        }
        setMessageOptionsOpen(false);
        setMessageTarget(target);
        setSendMessageModalOpen(true);
    };

    useEffect(() => {
        if (error) {
            setError('');
        }
    }, [startDate, endDate, selectedFields, reportType, selectedPlanId, priceFilterMode, exactPrice, minPrice, maxPrice, onlyActiveContracts]);

    useEffect(() => {
        const loadAffiliateCurrency = async () => {
            if (!affiliateId) return;
            try {
                const affiliate = await getAffiliateById(affiliateId);
                setPaymentCurrency(affiliate?.paymentCurrency || 'EUR');
            } catch (error) {
                setPaymentCurrency('EUR');
            }
        };

        loadAffiliateCurrency();
    }, [affiliateId]);

    const activeCategory = REPORT_CATEGORIES.find((category) => category.id === reportCategory) || REPORT_CATEGORIES[0];
    const revenueChartData = {
        labels: reportData.map((item) => item.label),
        datasets: [
            {
                label: 'Contracts',
                data: reportData.map((item) => getDisplayRevenueAmount(item.contracts || 0)),
                backgroundColor: '#0052cc',
                borderColor: '#003a99',
                borderWidth: 1.5,
                barPercentage: 0.55,
                categoryPercentage: 0.62,
                maxBarThickness: 24
            },
            {
                label: 'Children Contracts',
                data: reportData.map((item) => getDisplayRevenueAmount(item.childrenContracts || 0)),
                backgroundColor: '#d81b60',
                borderColor: '#ad1457',
                borderWidth: 1.5,
                barPercentage: 0.55,
                categoryPercentage: 0.62,
                maxBarThickness: 24
            },
            {
                label: 'Plans',
                data: reportData.map((item) => getDisplayRevenueAmount(item.plans || 0)),
                backgroundColor: '#f57c00',
                borderColor: '#e65100',
                borderWidth: 1.5,
                barPercentage: 0.55,
                categoryPercentage: 0.62,
                maxBarThickness: 24
            }
        ]
    };

    const revenueChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top'
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false
                },
                ticks: {
                    color: '#111827',
                    font: {
                        weight: 600
                    }
                }
            },
            y: {
                stacked: true,
                grid: {
                    color: 'rgba(17, 24, 39, 0.12)'
                },
                ticks: {
                    color: '#111827',
                    callback: (value) => formatRevenueAmount(Number(value))
                }
            }
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f8fafc' }}>
            <Box sx={{ mb: 2.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Affiliate Reports
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Pick a category, select a report, then generate and export results.
                </Typography>
            </Box>

            <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)' }}>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                                Report Categories
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                    gap: 1.25
                                }}
                            >
                                {REPORT_CATEGORIES.map((category) => {
                                    const isActive = reportCategory === category.id;
                                    return (
                                        <Button
                                            key={category.id}
                                            fullWidth
                                            variant={isActive ? 'contained' : 'outlined'}
                                            onClick={() => setReportCategory(category.id)}
                                            sx={{
                                                minHeight: 48,
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                background: isActive ? 'linear-gradient(120deg, #2563eb 0%, #0f766e 100%)' : undefined,
                                                borderColor: isActive ? 'transparent' : '#cbd5e1',
                                                color: isActive ? '#fff' : '#334155'
                                            }}
                                        >
                                            {category.label}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                                Select Report
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
                                    gap: 1.25
                                }}
                            >
                                {activeCategory.types.map((value) => {
                                    const isSelected = reportType === value;
                                    return (
                                        <Button
                                            key={value}
                                            fullWidth
                                            variant={isSelected ? 'contained' : 'outlined'}
                                            onClick={() => handleReportTypeChange(value)}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                px: 2,
                                                py: 1.1,
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                borderColor: '#cbd5e1',
                                                backgroundColor: isSelected ? '#0f766e' : '#fff',
                                                color: isSelected ? '#fff' : '#0f172a',
                                                '&:hover': {
                                                    backgroundColor: isSelected ? '#0d5f58' : '#f8fafc',
                                                    borderColor: '#94a3b8'
                                                }
                                            }}
                                        >
                                            {REPORT_TYPE_LABELS[value]}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Grid>

                        {(reportType === REPORT_TYPES.NEW_USERS || reportType === REPORT_TYPES.VISIT_COUNT || reportType === REPORT_TYPES.CHILDREN_VISIT_COUNT ||
                          reportType === REPORT_TYPES.PLAN_PURCHASE || reportType === REPORT_TYPES.CLASS_ATTENDANCE ||
                          reportType === REPORT_TYPES.CLASS_TYPE_ATTENDANCE || reportType === REPORT_TYPES.CONTRACT_EXPIRY ||
                          reportType === REPORT_TYPES.MEMBER_LTV || reportType === REPORT_TYPES.REVENUE_ANALYSIS || reportType === REPORT_TYPES.PEAK_HOURS ||
                          reportType === REPORT_TYPES.CLIENT_LIFECYCLE || reportType === REPORT_TYPES.PAYMENT_HOLIDAY) && (
                            <Grid item xs={12} md={reportType === REPORT_TYPES.PLAN_PURCHASE ? 12 : (reportType === REPORT_TYPES.VISIT_COUNT ? 6 : 6)}>
                                <Typography variant="h6" gutterBottom>
                                    Date Range
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                    <TextField
                                        label="Start Date"
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                    />
                                    <TextField
                                        label="End Date"
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                    />
                                </Box>
                            </Grid>
                        )}

                        {(reportType === REPORT_TYPES.VISIT_COUNT || reportType === REPORT_TYPES.CHILDREN_VISIT_COUNT) && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                    Visit Filter
                                </Typography>
                                <TextField
                                    label="Minimum Visits"
                                    type="number"
                                    value={minVisits}
                                    onChange={(e) => setMinVisits(Math.max(1, parseInt(e.target.value) || 1))}
                                    inputProps={{ min: 1 }}
                                    fullWidth
                                    helperText={reportType === REPORT_TYPES.CHILDREN_VISIT_COUNT
                                        ? "Show children with at least this many visits"
                                        : "Show clients with at least this many visits"}
                                />
                            </Grid>
                        )}

                        {reportType === REPORT_TYPES.PLAN_PURCHASE && (
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Select Plan
                                </Typography>
                                <FormControl fullWidth>
                                    <InputLabel id="plan-select-label">Plan</InputLabel>
                                    <Select
                                        labelId="plan-select-label"
                                        value={selectedPlanId}
                                        label="Plan"
                                        onChange={(e) => setSelectedPlanId(e.target.value)}
                                    >
                                        {plans.map(plan => (
                                            <MenuItem key={plan.id} value={plan.id}>
                                                {plan.name} - {formatMoney(plan.price)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {reportType === REPORT_TYPES.CLIENT_LIFECYCLE && (
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    ABC Course Package (Optional)
                                </Typography>
                                <FormControl fullWidth>
                                    <InputLabel id="abc-plan-select-label">Select ABC Course Package</InputLabel>
                                    <Select
                                        labelId="abc-plan-select-label"
                                        value={abcPlanId}
                                        label="Select ABC Course Package"
                                        onChange={(e) => setAbcPlanId(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {plans.map(plan => (
                                            <MenuItem key={plan.id} value={plan.id}>
                                                {plan.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {reportType === REPORT_TYPES.CLASS_TYPE_ATTENDANCE && (
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Select Class Type
                                </Typography>
                                <FormControl fullWidth>
                                    <InputLabel id="class-type-label">Class Type</InputLabel>
                                    <Select
                                        labelId="class-type-label"
                                        value={classType}
                                        label="Class Type"
                                        onChange={(e) => setClassType(e.target.value)}
                                    >
                                        {CLASS_TYPES.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {reportType === REPORT_TYPES.CONTRACT_PRICE && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        p: { xs: 2, sm: 2.5 },
                                        borderRadius: 3,
                                        border: '1px solid #e2e8f0',
                                        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        Contract Price Filter
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 2 }}>
                                        Choose how to match contract amounts, then optionally limit the list to active contracts.
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                                            gap: 1.25
                                        }}
                                    >
                                        {PRICE_FILTER_MODES.map((mode) => {
                                            const isSelected = priceFilterMode === mode.value;
                                            return (
                                                <Button
                                                    key={mode.value}
                                                    fullWidth
                                                    variant={isSelected ? 'contained' : 'outlined'}
                                                    onClick={() => setPriceFilterMode(mode.value)}
                                                    sx={{
                                                        minHeight: 64,
                                                        px: 2,
                                                        py: 1.25,
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        alignItems: 'flex-start',
                                                        justifyContent: 'center',
                                                        flexDirection: 'column',
                                                        backgroundColor: isSelected ? '#0f766e' : '#fff',
                                                        borderColor: isSelected ? 'transparent' : '#cbd5e1',
                                                        color: isSelected ? '#fff' : '#0f172a',
                                                        boxShadow: isSelected ? '0 8px 18px rgba(15, 118, 110, 0.18)' : 'none',
                                                        '&:hover': {
                                                            backgroundColor: isSelected ? '#0d5f58' : '#f8fafc',
                                                            borderColor: isSelected ? 'transparent' : '#94a3b8'
                                                        }
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
                                                        {mode.label}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 12, mt: 0.4, opacity: 0.82, fontWeight: 400 }}>
                                                        {mode.description}
                                                    </Typography>
                                                </Button>
                                            );
                                        })}
                                    </Box>

                                    {priceFilterMode === 'exact' && (
                                        <Box sx={{ mt: 2.25 }}>
                                            <TextField
                                                label="Exact price"
                                                type="number"
                                                value={exactPrice}
                                                onChange={(e) => setExactPrice(e.target.value)}
                                                inputProps={{ min: 0, step: '0.01' }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            {paymentCurrency === 'PLN' ? 'zł' : '€'}
                                                        </InputAdornment>
                                                    )
                                                }}
                                                sx={{
                                                    maxWidth: 280,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: '#fff'
                                                    }
                                                }}
                                                helperText="Only contracts with this exact monthly price"
                                            />
                                        </Box>
                                    )}

                                    {priceFilterMode === 'range' && (
                                        <Box
                                            sx={{
                                                mt: 2.25,
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
                                                gap: 1.5,
                                                alignItems: 'center'
                                            }}
                                        >
                                            <TextField
                                                label="Minimum"
                                                type="number"
                                                value={minPrice}
                                                onChange={(e) => setMinPrice(e.target.value)}
                                                inputProps={{ min: 0, step: '0.01' }}
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            {paymentCurrency === 'PLN' ? 'zł' : '€'}
                                                        </InputAdornment>
                                                    )
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: '#fff'
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    display: { xs: 'none', sm: 'block' },
                                                    color: '#94a3b8',
                                                    fontWeight: 700
                                                }}
                                            >
                                                –
                                            </Typography>
                                            <TextField
                                                label="Maximum"
                                                type="number"
                                                value={maxPrice}
                                                onChange={(e) => setMaxPrice(e.target.value)}
                                                inputProps={{ min: 0, step: '0.01' }}
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            {paymentCurrency === 'PLN' ? 'zł' : '€'}
                                                        </InputAdornment>
                                                    )
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: '#fff'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}

                                    <Box
                                        sx={{
                                            mt: 2.25,
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                            gap: 1.25
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 1.5,
                                                borderRadius: 2,
                                                border: '1px solid #e2e8f0',
                                                backgroundColor: '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
                                                    Only active contracts
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    Hide ended and not-yet-started contracts
                                                </Typography>
                                            </Box>
                                            <Switch
                                                checked={onlyActiveContracts}
                                                onChange={(e) => {
                                                    const next = e.target.checked;
                                                    setOnlyActiveContracts(next);
                                                    if (contractPriceTotals) {
                                                        generateReport({ onlyActive: next });
                                                    }
                                                }}
                                                inputProps={{ 'aria-label': 'Only active contracts' }}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: '#0f766e'
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: '#0f766e'
                                                    }
                                                }}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 1.5,
                                                borderRadius: 2,
                                                border: '1px solid #e2e8f0',
                                                backgroundColor: '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
                                                    Show member details
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    Name, contract start date and price
                                                </Typography>
                                            </Box>
                                            <Switch
                                                checked={showContractDetails}
                                                onChange={(e) => setShowContractDetails(e.target.checked)}
                                                inputProps={{ 'aria-label': 'Show member details' }}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: '#0f766e'
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: '#0f766e'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        )}

                        {reportType === REPORT_TYPES.DEMOGRAPHICS && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Member Status
                                    </Typography>
                                    <FormControl fullWidth>
                                        <InputLabel id="filter-type-label">Filter By</InputLabel>
                                        <Select
                                            labelId="filter-type-label"
                                            value={filterType}
                                            label="Filter By"
                                            onChange={(e) => setFilterType(e.target.value)}
                                        >
                                            <MenuItem value="all">All Members</MenuItem>
                                            <MenuItem value="active">Active Members Only</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Demographic Filters
                                    </Typography>
                                    {demographicFilters.map((filter, index) => (
                                        <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={3}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Sex</InputLabel>
                                                        <Select
                                                            value={filter.sex}
                                                            label="Sex"
                                                            onChange={(e) => updateDemographicFilter(index, 'sex', e.target.value)}
                                                        >
                                                            <MenuItem value="all">All</MenuItem>
                                                            <MenuItem value="male">Male</MenuItem>
                                                            <MenuItem value="female">Female</MenuItem>
                                                            <MenuItem value="other">Other</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label="Min Age"
                                                        type="number"
                                                        value={filter.minAge || ''}
                                                        onChange={(e) => updateDemographicFilter(index, 'minAge', e.target.value ? parseInt(e.target.value) : null)}
                                                        inputProps={{ min: 0, max: 120 }}
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label="Max Age"
                                                        type="number"
                                                        value={filter.maxAge || ''}
                                                        onChange={(e) => updateDemographicFilter(index, 'maxAge', e.target.value ? parseInt(e.target.value) : null)}
                                                        inputProps={{ min: 0, max: 120 }}
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => removeDemographicFilter(index)}
                                                        disabled={demographicFilters.length === 1}
                                                        fullWidth
                                                    >
                                                        Remove
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        onClick={addDemographicFilter}
                                        startIcon={<span>+</span>}
                                    >
                                        Add Filter
                                    </Button>
                                </Grid>
                            </>
                        )}

                        {reportType !== REPORT_TYPES.DEMOGRAPHICS && 
                         reportType !== REPORT_TYPES.PLAN_PURCHASE && 
                         reportType !== REPORT_TYPES.CLIENT_LIFECYCLE && 
                         reportType !== REPORT_TYPES.REVENUE_ANALYSIS &&
                         reportType !== REPORT_TYPES.CLASS_TYPE_ATTENDANCE &&
                         reportType !== REPORT_TYPES.CONTRACT_PRICE &&
                         reportType !== REPORT_TYPES.PAYMENT_HOLIDAY && (
                            <Grid item xs={12} md={(reportType === REPORT_TYPES.NEW_USERS || reportType === REPORT_TYPES.VISIT_COUNT || reportType === REPORT_TYPES.CHILDREN_VISIT_COUNT) ? 12 : 12}>
                                <Typography variant="h6" gutterBottom>
                                    Select Data Fields
                                </Typography>

                                <FormControl component="fieldset" sx={{ width: '100%' }}>
                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleSelectAll}
                                            disabled={isLoading}
                                        >
                                            {selectedFields.length === availableFields.length ? 'Deselect All' : 'Select All'}
                                        </Button>
                                    </Box>

                                    <FormGroup>
                                        <Grid container spacing={1}>
                                            {availableFields.map((field) => (
                                                <Grid item xs={12} sm={6} md={4} key={field.key}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={selectedFields.includes(field.key)}
                                                                onChange={() => handleFieldChange(field.key)}
                                                                disabled={isLoading}
                                                            />
                                                        }
                                                        label={field.label}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </FormGroup>

                                    {availableFields.length === 0 && !isLoading && (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            No fields available for this report type
                                        </Alert>
                                    )}
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            onClick={generateReport}
                            disabled={isLoading || (![REPORT_TYPES.DEMOGRAPHICS, REPORT_TYPES.PLAN_PURCHASE, 
                                     REPORT_TYPES.CLASS_ATTENDANCE, REPORT_TYPES.CONTRACT_EXPIRY, 
                                     REPORT_TYPES.MEMBER_LTV, REPORT_TYPES.REVENUE_ANALYSIS, REPORT_TYPES.PEAK_HOURS,
                                     REPORT_TYPES.CREDIT_INFO, REPORT_TYPES.CLIENT_LIFECYCLE, 
                                     REPORT_TYPES.CLASS_TYPE_ATTENDANCE, REPORT_TYPES.CONTRACT_PRICE,
                                     REPORT_TYPES.PAYMENT_HOLIDAY].includes(reportType) && selectedFields.length === 0)}
                        >
                            {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                            {isLoading ? 'Loading...' : 'Generate Report'}
                        </Button>
                        {reportData.length > 0 && (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={exportToCSV}
                                    disabled={isLoading}
                                >
                                    Export CSV
                                </Button>
                                {reportType === REPORT_TYPES.CLASS_TYPE_ATTENDANCE && (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => handleSendMessage('selectedUsers')}
                                        disabled={selectedUsers.length === 0}
                                    >
                                        Send Message ({selectedUsers.length})
                                    </Button>
                                )}
                                {reportType === REPORT_TYPES.CONTRACT_PRICE && (
                                    <>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            startIcon={<EmailIcon />}
                                            onClick={() => handleSendMessage('contractPriceAll')}
                                            disabled={contractPriceRecipients.length === 0}
                                        >
                                            Send Email ({contractPriceRecipients.length})
                                        </Button>
                                        {showContractDetails && (
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                startIcon={<EmailIcon />}
                                                onClick={() => handleSendMessage('selectedUsers')}
                                                disabled={selectedUsers.length === 0}
                                            >
                                                Send to Selected ({selectedUsers.length})
                                            </Button>
                                        )}
                                    </>
                                )}
                                {reportType === REPORT_TYPES.NEW_USERS && (
                                    <Button
                                        variant="contained"
                                        onClick={() => setMessageOptionsOpen(true)}
                                        disabled={newUsersForMessaging.length === 0 && guestUsersForMessaging.length === 0}
                                        startIcon={<EmailIcon />}
                                    >
                                        Message
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {reportData.length > 0 && 
             reportType !== REPORT_TYPES.DEMOGRAPHICS && 
             reportType !== REPORT_TYPES.PLAN_PURCHASE &&
             reportType !== REPORT_TYPES.CLASS_ATTENDANCE &&
             reportType !== REPORT_TYPES.CLASS_TYPE_ATTENDANCE &&
             reportType !== REPORT_TYPES.CONTRACT_EXPIRY &&
             reportType !== REPORT_TYPES.MEMBER_LTV &&
             reportType !== REPORT_TYPES.REVENUE_ANALYSIS &&
             reportType !== REPORT_TYPES.PEAK_HOURS &&
             reportType !== REPORT_TYPES.CREDIT_INFO &&
             reportType !== REPORT_TYPES.CLIENT_LIFECYCLE &&
             reportType !== REPORT_TYPES.CONTRACT_PRICE &&
             reportType !== REPORT_TYPES.PAYMENT_HOLIDAY && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {REPORT_TYPE_LABELS[reportType]} Results ({reportData.length} records)
                        </Typography>

                        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {selectedFields.map((fieldKey) => {
                                            const field = availableFields.find(f => f.key === fieldKey);
                                            return (
                                                <TableCell key={fieldKey}>
                                                    <TableSortLabel
                                                        active={orderBy === fieldKey}
                                                        direction={orderBy === fieldKey ? order : 'asc'}
                                                        onClick={() => handleSort(fieldKey)}
                                                    >
                                                        {field ? field.label : fieldKey}
                                                    </TableSortLabel>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row, index) => (
                                        <TableRow key={row.id || index}>
                                            {selectedFields.map((field) => (
                                                <TableCell key={field}>
                                                    {formatCellValue(row[field], field)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {reportType === REPORT_TYPES.NEW_USERS && guestReportData.length > 0 && (
                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Guest Registrations ({guestReportData.length} records)
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>First Name</strong></TableCell>
                                        <TableCell><strong>Last Name</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Registered At</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {guestReportData.map((guest) => (
                                        <TableRow key={guest.id}>
                                            <TableCell>{guest.firstName || '-'}</TableCell>
                                            <TableCell>{guest.lastName || '-'}</TableCell>
                                            <TableCell>{guest.email || '-'}</TableCell>
                                            <TableCell>{formatReportDate(guest.createdAt)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

                        {reportData.length > 0 && reportType === REPORT_TYPES.CLASS_ATTENDANCE && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Class Attendance Report Results ({reportData.length} classes)
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Training Name</strong></TableCell>
                                        <TableCell><strong>Type</strong></TableCell>
                                        <TableCell><strong>Date & Time</strong></TableCell>
                                        <TableCell><strong>Day</strong></TableCell>
                                        <TableCell><strong>Trainer</strong></TableCell>
                                        <TableCell align="right"><strong>Capacity</strong></TableCell>
                                        <TableCell align="right"><strong>Registered</strong></TableCell>
                                        <TableCell align="right"><strong>Waitlist</strong></TableCell>
                                        <TableCell align="right"><strong>Utilization %</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.trainingName}</TableCell>
                                            <TableCell>{row.trainingType}</TableCell>
                                            <TableCell>{formatReportDateTime(row.time)}</TableCell>
                                            <TableCell>{row.dayOfWeek}</TableCell>
                                            <TableCell>{row.trainer}</TableCell>
                                            <TableCell align="right">{row.capacity}</TableCell>
                                            <TableCell align="right">{row.registered}</TableCell>
                                            <TableCell align="right">{row.waitlist}</TableCell>
                                            <TableCell align="right">{row.utilizationRate}%</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {reportData.length > 0 && reportType === REPORT_TYPES.CONTRACT_EXPIRY && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Contract Expiry Report Results ({reportData.length} contracts)
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Member Name</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Phone</strong></TableCell>
                                        <TableCell><strong>Contract Name</strong></TableCell>
                                        <TableCell align="right"><strong>Payment Amount</strong></TableCell>
                                        <TableCell><strong>Next Payment Date</strong></TableCell>
                                        <TableCell align="right"><strong>Days Until</strong></TableCell>
                                        <TableCell align="right"><strong>Months Active</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.memberName}</TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>{row.phone || '-'}</TableCell>
                                            <TableCell>{row.contractName}</TableCell>
                                            <TableCell align="right">{formatMoney(row.paymentAmount)}</TableCell>
                                            <TableCell>{formatReportDate(row.nextPaymentDate)}</TableCell>
                                            <TableCell align="right">{row.daysUntilExpiry}</TableCell>
                                            <TableCell align="right">{row.monthsActive}</TableCell>
                                            <TableCell>{row.status}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {reportData.length > 0 && reportType === REPORT_TYPES.MEMBER_LTV && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Member Lifetime Value Report Results ({reportData.length} members)
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Member Name</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Phone</strong></TableCell>
                                        <TableCell align="right"><strong>Total Spent</strong></TableCell>
                                        <TableCell align="right"><strong>Months Active</strong></TableCell>
                                        <TableCell align="right"><strong>Avg Monthly</strong></TableCell>
                                        <TableCell align="right"><strong>Purchase Count</strong></TableCell>
                                        <TableCell align="right"><strong>Monthly Recurring</strong></TableCell>
                                        <TableCell align="right"><strong>Predicted LTV</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.memberName}</TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>{row.phone || '-'}</TableCell>
                                            <TableCell align="right">{formatMoney(row.totalSpent)}</TableCell>
                                            <TableCell align="right">{row.monthsActive}</TableCell>
                                            <TableCell align="right">{formatMoney(row.avgMonthlySpend)}</TableCell>
                                            <TableCell align="right">{row.purchaseCount}</TableCell>
                                            <TableCell align="right">{formatMoney(row.monthlyRecurring)}</TableCell>
                                            <TableCell align="right">{formatMoney(row.predictedLTV)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {reportData.length > 0 && reportType === REPORT_TYPES.PEAK_HOURS && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Peak Hours Analysis Results
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Day</strong></TableCell>
                                        <TableCell><strong>Time Slot</strong></TableCell>
                                        <TableCell align="right"><strong>Class Count</strong></TableCell>
                                        <TableCell align="right"><strong>Avg Registrations</strong></TableCell>
                                        <TableCell align="right"><strong>Total Capacity</strong></TableCell>
                                        <TableCell align="right"><strong>Total Registrations</strong></TableCell>
                                        <TableCell align="right"><strong>Utilization %</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{row.day}</TableCell>
                                            <TableCell>{row.timeSlot}</TableCell>
                                            <TableCell align="right">{row.classCount}</TableCell>
                                            <TableCell align="right">{row.avgRegistrations}</TableCell>
                                            <TableCell align="right">{row.totalCapacity}</TableCell>
                                            <TableCell align="right">{row.totalRegistrations}</TableCell>
                                            <TableCell align="right">{row.utilizationRate}%</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {reportData.length > 0 && reportType === REPORT_TYPES.CREDIT_INFO && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Credit Info Report Results ({reportData.length} members with credit)
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Member Name</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Phone</strong></TableCell>
                                        <TableCell align="right"><strong>Current Credit</strong></TableCell>
                                        <TableCell align="right"><strong>Total Added</strong></TableCell>
                                        <TableCell align="right"><strong>Total Used</strong></TableCell>
                                        <TableCell align="right"><strong>Transaction Count</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.memberName}</TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>{row.phone || '-'}</TableCell>
                                            <TableCell align="right">{formatMoney(row.currentCredit)}</TableCell>
                                            <TableCell align="right">{formatMoney(row.totalAdded)}</TableCell>
                                            <TableCell align="right">{formatMoney(row.totalUsed)}</TableCell>
                                            <TableCell align="right">{row.transactionCount}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {contractPriceTotals && reportType === REPORT_TYPES.CONTRACT_PRICE && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Contracts by Price ({contractPriceTotals.totalPeople} people, {contractPriceTotals.totalContracts} contracts)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {onlyActiveContracts ? 'Active contracts only. ' : 'All accepted contracts. '}
                            Total monthly amount: {formatMoney(contractPriceTotals.totalMonthlyAmount)}
                        </Typography>

                        {contractPriceSummary.length === 0 ? (
                            <Alert severity="info">No active accepted contracts match this price filter.</Alert>
                        ) : (
                            <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 400 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Price</strong></TableCell>
                                            <TableCell align="right"><strong>People</strong></TableCell>
                                            <TableCell align="right"><strong>Contracts</strong></TableCell>
                                            <TableCell align="right"><strong>Monthly Total</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {contractPriceSummary.map((row) => (
                                            <TableRow key={row.price}>
                                                <TableCell>{formatMoney(row.price)}</TableCell>
                                                <TableCell align="right">{row.peopleCount}</TableCell>
                                                <TableCell align="right">{row.contractCount}</TableCell>
                                                <TableCell align="right">{formatMoney(row.monthlyTotal)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {showContractDetails && reportData.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="h6">
                                        Contract details ({reportData.length})
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={handleSelectAllUsers}
                                    >
                                        {selectedUsers.length === contractPriceRecipients.length && contractPriceRecipients.length > 0
                                            ? 'Deselect All'
                                            : 'Select All'}
                                    </Button>
                                </Box>
                                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedUsers.length === contractPriceRecipients.length && contractPriceRecipients.length > 0}
                                                        indeterminate={selectedUsers.length > 0 && selectedUsers.length < contractPriceRecipients.length}
                                                        onChange={handleSelectAllUsers}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'fullName'}
                                                        direction={orderBy === 'fullName' ? order : 'asc'}
                                                        onClick={() => handleSort('fullName')}
                                                    >
                                                        <strong>Name</strong>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'startDate'}
                                                        direction={orderBy === 'startDate' ? order : 'asc'}
                                                        onClick={() => handleSort('startDate')}
                                                    >
                                                        <strong>Contract start</strong>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'status'}
                                                        direction={orderBy === 'status' ? order : 'asc'}
                                                        onClick={() => handleSort('status')}
                                                    >
                                                        <strong>Status</strong>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <TableSortLabel
                                                        active={orderBy === 'paymentAmount'}
                                                        direction={orderBy === 'paymentAmount' ? order : 'asc'}
                                                        onClick={() => handleSort('paymentAmount')}
                                                    >
                                                        <strong>Price</strong>
                                                    </TableSortLabel>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData.map((row) => {
                                                const isSelected = selectedUsers.some(u => u.id === row.userId);
                                                return (
                                                    <TableRow
                                                        key={row.id}
                                                        hover
                                                        selected={isSelected}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={() => handleSelectUser({
                                                                    id: row.userId,
                                                                    fullName: row.fullName,
                                                                    email: row.email
                                                                })}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{row.fullName || '-'}</TableCell>
                                                        <TableCell>
                                                            {formatReportDate(row.startDate)}
                                                        </TableCell>
                                                        <TableCell>{row.status || '-'}</TableCell>
                                                        <TableCell align="right">{formatMoney(row.paymentAmount)}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {paymentHolidayTotals && reportType === REPORT_TYPES.PAYMENT_HOLIDAY && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Payment Holidays ({paymentHolidayTotals.totalPeople} people, {paymentHolidayTotals.totalHolidays} holidays)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Approved payment holidays that overlap the selected period.
                        </Typography>

                        {reportData.length === 0 ? (
                            <Alert severity="info">No approved payment holidays overlap this date range.</Alert>
                        ) : (
                            <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 600 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={orderBy === 'fullName'}
                                                    direction={orderBy === 'fullName' ? order : 'asc'}
                                                    onClick={() => handleSort('fullName')}
                                                >
                                                    <strong>Name</strong>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={orderBy === 'startDate'}
                                                    direction={orderBy === 'startDate' ? order : 'asc'}
                                                    onClick={() => handleSort('startDate')}
                                                >
                                                    <strong>Start date</strong>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={orderBy === 'endDate'}
                                                    direction={orderBy === 'endDate' ? order : 'asc'}
                                                    onClick={() => handleSort('endDate')}
                                                >
                                                    <strong>End date</strong>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={orderBy === 'reason'}
                                                    direction={orderBy === 'reason' ? order : 'asc'}
                                                    onClick={() => handleSort('reason')}
                                                >
                                                    <strong>Reason</strong>
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>{row.fullName || '-'}</TableCell>
                                                <TableCell>
                                                    {formatReportDate(row.startDate)}
                                                </TableCell>
                                                <TableCell>
                                                    {formatReportDate(row.endDate)}
                                                </TableCell>
                                                <TableCell>{row.reason || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            )}

            {reportData.length > 0 && reportType === REPORT_TYPES.CLASS_TYPE_ATTENDANCE && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                {classType} Attendance Results ({reportData.length} members)
                            </Typography>
                            <Button
                                size="small"
                                onClick={handleSelectAllUsers}
                            >
                                {selectedUsers.length === reportData.length ? 'Deselect All' : 'Select All'}
                            </Button>
                        </Box>

                        <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedUsers.length === reportData.length && reportData.length > 0}
                                                indeterminate={selectedUsers.length > 0 && selectedUsers.length < reportData.length}
                                                onChange={handleSelectAllUsers}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'fullName'}
                                                direction={orderBy === 'fullName' ? order : 'asc'}
                                                onClick={() => handleSort('fullName')}
                                            >
                                                <strong>Name</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'email'}
                                                direction={orderBy === 'email' ? order : 'asc'}
                                                onClick={() => handleSort('email')}
                                            >
                                                <strong>Email</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Phone</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={orderBy === 'attendanceCount'}
                                                direction={orderBy === 'attendanceCount' ? order : 'asc'}
                                                onClick={() => handleSort('attendanceCount')}
                                            >
                                                <strong>Attendance Count</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row) => {
                                        const isSelected = selectedUsers.some(u => u.id === row.userId);
                                        return (
                                            <TableRow 
                                                key={row.userId}
                                                hover
                                                selected={isSelected}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={() => handleSelectUser({
                                                            id: row.userId,
                                                            fullName: row.fullName,
                                                            email: row.email
                                                        })}
                                                    />
                                                </TableCell>
                                                <TableCell>{row.fullName}</TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>{row.phone || '-'}</TableCell>
                                                <TableCell align="right">
                                                    <strong>{row.attendanceCount}</strong>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {reportData.length > 0 && reportType === REPORT_TYPES.DEMOGRAPHICS && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Demographics Report Results
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Filter: {filterType === 'all' ? 'All Members' : 'Active Members Only'}
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Sex</strong></TableCell>
                                        <TableCell><strong>Age Range</strong></TableCell>
                                        <TableCell align="right"><strong>Count</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.sex}</TableCell>
                                            <TableCell>{row.ageRange}</TableCell>
                                            <TableCell align="right">{row.count}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {lifecycleData && reportType === REPORT_TYPES.CLIENT_LIFECYCLE && (
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2 }}>
                        Client Lifecycle Report
                    </Typography>

                    {/* Guest Analysis Card */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                Guest Registrations Analysis
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                        <Typography variant="h4">{lifecycleData.guestAnalysis?.totalGuests || 0}</Typography>
                                        <Typography variant="body2">Total Guests</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                                        <Typography variant="h4">{lifecycleData.guestAnalysis?.guestsWhoPurchasedPlans || 0}</Typography>
                                        <Typography variant="body2">Guests Who Purchased Plans</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                                        <Typography variant="h4">{lifecycleData.guestAnalysis?.conversionRate || 0}%</Typography>
                                        <Typography variant="body2">Conversion Rate</Typography>
                                    </Card>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Members Analysis Card */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                Members Analysis
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                        <Typography variant="h4">{lifecycleData.membersAnalysis?.totalMembers || 0}</Typography>
                                        <Typography variant="body2">Total Members</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                                        <Typography variant="h4">{lifecycleData.membersAnalysis?.activeMembers || 0}</Typography>
                                        <Typography variant="body2">Active Members</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                                        <Typography variant="h4">{lifecycleData.membersAnalysis?.newMembers || 0}</Typography>
                                        <Typography variant="body2">New Members</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                                        <Typography variant="h4">{lifecycleData.membersAnalysis?.newMembersWithActivePlans || 0}</Typography>
                                        <Typography variant="body2">New Members with Active Plans</Typography>
                                    </Card>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Client Tenure Card */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                Client Tenure Analysis
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" gutterBottom>Days Active</Typography>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.days30 || 0}</Typography>
                                        <Typography variant="caption">30+ Days</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.days60 || 0}</Typography>
                                        <Typography variant="caption">60+ Days</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.days90 || 0}</Typography>
                                        <Typography variant="caption">90+ Days</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.days120 || 0}</Typography>
                                        <Typography variant="caption">120+ Days</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.days180 || 0}</Typography>
                                        <Typography variant="caption">180+ Days</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.days365 || 0}</Typography>
                                        <Typography variant="caption">365+ Days</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="subtitle1" gutterBottom>Years Active</Typography>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.years1 || 0}</Typography>
                                        <Typography variant="caption">1+ Year</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.years2 || 0}</Typography>
                                        <Typography variant="caption">2+ Years</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.years3 || 0}</Typography>
                                        <Typography variant="caption">3+ Years</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.years4 || 0}</Typography>
                                        <Typography variant="caption">4+ Years</Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                        <Typography variant="h6">{lifecycleData.clientTenure?.years5 || 0}</Typography>
                                        <Typography variant="caption">5+ Years</Typography>
                                    </Card>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* ABC Analysis Card */}
                    {lifecycleData.abcAnalysis && (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                    ABC Course Package Analysis
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                    Package: <strong>{lifecycleData.abcAnalysis.planName}</strong>
                                </Typography>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                            <Typography variant="h4">{lifecycleData.abcAnalysis.totalClientsWithABC || 0}</Typography>
                                            <Typography variant="body2">Clients with ABC Package</Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                                            <Typography variant="h4">{lifecycleData.abcAnalysis.clientsWithPostABCPlan || 0}</Typography>
                                            <Typography variant="body2">Clients Purchased Plan After ABC</Typography>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* Contract Analysis Card */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                Contract Analysis
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom>Contracts Started</Typography>
                                    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                                        <Typography variant="h5">{lifecycleData.contractAnalysis?.started?.total || 0}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Regular: {lifecycleData.contractAnalysis?.started?.regular || 0} | 
                                            Family: {lifecycleData.contractAnalysis?.started?.family || 0}
                                        </Typography>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom>Contracts Ended</Typography>
                                    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                                        <Typography variant="h5">{lifecycleData.contractAnalysis?.ended?.total || 0}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Regular: {lifecycleData.contractAnalysis?.ended?.regular || 0} | 
                                            Family: {lifecycleData.contractAnalysis?.ended?.family || 0}
                                        </Typography>
                                    </Card>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* First Training Analysis Card */}
                    {lifecycleData.firstTrainingAnalysis && (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                    First Training Analysis
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="h4">{lifecycleData.firstTrainingAnalysis.totalMembersInPeriod || 0}</Typography>
                                            <Typography variant="body2">Total Members in Period</Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                                            <Typography variant="h4">{lifecycleData.firstTrainingAnalysis.membersWithFirstTraining || 0}</Typography>
                                            <Typography variant="body2">Members with First Training</Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                                            <Typography variant="h4">{lifecycleData.firstTrainingAnalysis.membersPurchasedPlan || 0}</Typography>
                                            <Typography variant="body2">Members Purchased Plan</Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                                            <Typography variant="h4">{lifecycleData.firstTrainingAnalysis.membersWithActivePlan || 0}</Typography>
                                            <Typography variant="body2">Members with Active Plan</Typography>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            )}

            {revenueData && reportType === REPORT_TYPES.REVENUE_ANALYSIS && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                                Revenue Analysis Report
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={includeVAT}
                                        onChange={(e) => setIncludeVAT(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={`${includeVAT ? 'With' : 'Without'} VAT (${revenueData?.vatRate || 0}%)`}
                            />
                        </Box>

                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Date range: {formatReportDate(startDate)} - {formatReportDate(endDate)}
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 2, mb: 3, overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Category</strong></TableCell>
                                        {reportData.map((monthRow) => (
                                            <TableCell key={monthRow.month} align="right">
                                                <strong>{monthRow.label}</strong>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>Total Purchases</TableCell>
                                        {reportData.map((monthRow) => (
                                            <TableCell key={`${monthRow.month}-totalPurchases`} align="right">
                                                {monthRow.totalPurchases || 0}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Contract Clients</TableCell>
                                        {reportData.map((monthRow) => (
                                            <TableCell key={`${monthRow.month}-contractClients`} align="right">
                                                {(monthRow.contractClientsCount || 0)}x ({formatRevenueAmount(monthRow.contracts || 0)})
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Children Contracts</TableCell>
                                        {reportData.map((monthRow) => (
                                            <TableCell key={`${monthRow.month}-childrenContracts`} align="right">
                                                {(monthRow.childrenContractsCount || 0)}x ({formatRevenueAmount(monthRow.childrenContracts || 0)})
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Contract Revenue</strong></TableCell>
                                        {reportData.map((monthRow) => (
                                            <TableCell key={`${monthRow.month}-contractRevenue`} align="right">
                                                <strong>{formatRevenueAmount((monthRow.contracts || 0) + (monthRow.childrenContracts || 0))}</strong>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Plan Packages Count</TableCell>
                                        {reportData.map((monthRow) => (
                                            <TableCell key={`${monthRow.month}-planPackagesCount`} align="right">
                                                {monthRow.planPackagesCount || 0}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Plan Revenue</strong></TableCell>
                                        {reportData.map((monthRow) => (
                                            <TableCell key={`${monthRow.month}-planRevenue`} align="right">
                                                <strong>{formatRevenueAmount(monthRow.plans || 0)}</strong>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Total Revenue</strong></TableCell>
                                        {reportData.map((monthRow) => (
                                            <TableCell key={`${monthRow.month}-totalRevenue`} align="right">
                                                <strong>{formatRevenueAmount(monthRow.totalRevenue || 0)}</strong>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography variant="h6" gutterBottom>
                            Monthly Revenue Breakdown
                        </Typography>
                        <Box sx={{ height: 360 }}>
                            <Bar data={revenueChartData} options={revenueChartOptions} />
                        </Box>
                    </CardContent>
                </Card>
            )}

            {reportData.length > 0 && reportType === REPORT_TYPES.PLAN_PURCHASE && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Plan Purchase Report Results
                        </Typography>
                        {planDetails && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Plan: {planDetails.name} - {formatMoney(planDetails.price)}
                            </Typography>
                        )}
                        <Typography variant="h6" color="primary" gutterBottom>
                            Total Purchases: {reportData.length} | Total Amount: {formatMoney(planPurchaseTotal)}
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Buyer Name</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Phone</strong></TableCell>
                                        <TableCell align="right"><strong>Amount</strong></TableCell>
                                        <TableCell><strong>Purchase Date</strong></TableCell>
                                        <TableCell><strong>Family Member</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.buyerName || '-'}</TableCell>
                                            <TableCell>{row.email || '-'}</TableCell>
                                            <TableCell>{row.phone || '-'}</TableCell>
                                            <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                                            <TableCell>{formatReportDate(row.purchaseDate)}</TableCell>
                                            <TableCell>{row.isFamilyMember ? 'Yes' : 'No'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}
            
            <Dialog
                open={messageOptionsOpen}
                onClose={() => setMessageOptionsOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Select recipients</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
                        <Button
                            variant="contained"
                            onClick={() => handleSendMessage('newMembers')}
                            disabled={newUsersForMessaging.length === 0}
                            sx={{
                                backgroundColor: '#1f1f1f',
                                color: '#ffffff',
                                '&:hover': { backgroundColor: '#000000' }
                            }}
                        >
                            Send to Members ({newUsersForMessaging.length})
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={() => handleSendMessage('inactiveNewMembers')}
                            disabled={inactiveNewUsersForMessaging.length === 0}
                        >
                            Send to Unactive Members ({inactiveNewUsersForMessaging.length})
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleSendMessage('newGuests')}
                            disabled={guestUsersForMessaging.length === 0}
                        >
                            Send to Guests ({guestUsersForMessaging.length})
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => handleSendMessage('allSelectedUsers')}
                            disabled={newUsersForMessaging.length === 0 && guestUsersForMessaging.length === 0}
                        >
                            All Selected Users
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <SendMessageModal
                open={sendMessageModalOpen}
                onClose={() => setSendMessageModalOpen(false)}
                affiliate={affiliateId}
                affiliateEmail={affiliateEmail}
                preSelectedUsers={messageTarget === 'selectedUsers'
                    ? selectedUsers
                    : messageTarget === 'contractPriceAll'
                        ? contractPriceRecipients
                    : messageTarget === 'newMembers'
                        ? newUsersForMessaging.map(({ id, fullName, email }) => ({ id, fullName, email }))
                        : messageTarget === 'inactiveNewMembers'
                        ? inactiveNewUsersForMessaging.map(({ id, fullName, email }) => ({ id, fullName, email }))
                        : messageTarget === 'allSelectedUsers'
                            ? newUsersForMessaging.map(({ id, fullName, email }) => ({ id, fullName, email }))
                            : []}
                preSelectedGuests={messageTarget === 'newGuests' || messageTarget === 'allSelectedUsers'
                    ? guestUsersForMessaging
                    : []}
            />
        </Box>
    );
}